/**
 * The exact expected value of every legal action, against the deck that is actually
 * left.
 *
 * ============================ WHAT "EXACT" MEANS HERE ==========================
 * Not a lookup, not a simulation, not a table: a full recursive expectation over the
 * remaining composition. Every card the player can see has been removed — their own
 * cards, every bot's cards, the dealer's upcard, and the cards from split hands that
 * have already been resolved — and the dealer's forced play is enumerated over what
 * remains, weighting each draw by how many of that card are left.
 *
 * That is what makes this stronger than a basic-strategy table, and the difference is
 * worth stating because it is the reason the room exists. A strategy table is computed
 * against a FULL shoe. Once four cards are on the table the composition has moved, and
 * a handful of borderline decisions move with it. This computes the decision in front
 * of you, not the average one.
 *
 * It is also why the word "solver" does not appear anywhere near this. Nothing here is
 * solved in the game-theory sense — the dealer has no choices to make. It is
 * enumeration, and enumeration is exact, which is a stronger claim than "solved" and a
 * true one.
 *
 * ## Composition, not cards
 *
 * Everything below works on a 10-slot vector of counts: 2..9 in their own slots, all
 * tens together in slot 8, aces in slot 9. Blackjack cannot tell a jack from a king,
 * so carrying 13 ranks would triple the work to represent a distinction the game does
 * not make.
 *
 * ## Memoisation and why the key is what it is
 *
 * Dealer outcomes are memoised on (upcard, total, soft, composition). The composition
 * is part of the key because it MUST be — the whole point is that the answer changes
 * with it — so the cache is per-decision rather than global, and it is handed in and
 * thrown away with the decision. A cache that outlived the decision would be a subtle
 * way of reintroducing the full-shoe approximation this file exists to avoid.
 * ================================================================================
 */
import {
  ACE_SLOT, COMP_SLOTS, SLOT_VALUE, type BlackjackRules,
} from "./rules";

/** Probabilities of each dealer end state, given the cards left. */
export interface DealerOutcome {
  /** Index 0..4 = final total 17,18,19,20,21; index 5 = bust. */
  p: Float64Array;
  /** Dealer made a natural. Only reachable from the initial two-card state. */
  pNatural: number;
}

const BUST = 5;

/**
 * A composition key, built without `join`.
 *
 * The memo key has to include the whole composition — that is the entire point of a
 * composition-dependent analysis — so it is built once per lookup and it is on the
 * hot path. `comp.join(",")` allocated a 30-character string and ran the number
 * formatter ten times per node; at a p95 of 195ms that was a visible share of the
 * decision. Each slot holds at most 32 cards in a two-deck shoe, so one character
 * each is plenty and `fromCharCode` does it in one call.
 */
function compKey(comp: Int32Array): string {
  return String.fromCharCode(
    comp[0]!, comp[1]!, comp[2]!, comp[3]!, comp[4]!,
    comp[5]!, comp[6]!, comp[7]!, comp[8]!, comp[9]!);
}

/**
 * Total after adding a card.
 *
 * `total` is the DISPLAYED total — it already counts a soft ace as eleven — and `soft`
 * means one ace in the hand is currently being counted that way and can still be
 * dropped to one.
 *
 * The obvious version of this is wrong, and wrong in a way that only shows up on hands
 * with several aces. Writing `soft = soft || value === 11` and then clearing the flag
 * on the first reduction loses the fact that an ace is STILL being counted as eleven.
 * Four aces then came out as 23 and busted — a hand that cannot bust — and the dealer's
 * bust rate with an ace up read 13.6% against a published 11.5%. Caught by comparing
 * the dealer distribution to known values before anything was built on top of it.
 *
 * So the two cases are separated. A new ace either becomes the soft one or comes in as
 * a one; and a bust is absorbed only while an ace is still up at eleven.
 */
function add(total: number, soft: boolean, value: number): { total: number; soft: boolean } {
  let t = total + value;
  let s = soft;
  if (value === 11) {
    if (t > 21) t -= 10;                 // this ace has to come in as a one
    else s = true;                       // and otherwise it is the soft one
  }
  if (t > 21 && s) { t -= 10; s = false; }   // drop the soft ace to absorb the bust
  return { total: t, soft: s };
}

/**
 * The dealer's distribution from a known partial hand, over a known composition.
 *
 * The dealer has no decisions — the rules decide every card — so this is a pure
 * expectation with no maximisation anywhere in it. That is what makes the whole
 * analysis tractable: only the player branches.
 */
export function dealerOutcome(
  comp: Int32Array, total: number, soft: boolean, rules: BlackjackRules,
  memo: Map<string, Float64Array> = new Map(),
): Float64Array {
  const stand = rules.dealerStandsSoft17
    ? total >= 17
    : total > 17 || (total === 17 && !soft);
  if (total > 21) { const out = new Float64Array(6); out[BUST] = 1; return out; }
  if (stand) { const out = new Float64Array(6); out[total - 17] = 1; return out; }

  let remaining = 0;
  for (let s = 0; s < COMP_SLOTS; s++) remaining += comp[s]!;
  if (remaining === 0) { const out = new Float64Array(6); out[BUST] = 1; return out; }

  const key = `${total}|${soft ? 1 : 0}|${compKey(comp)}`;
  const hit = memo.get(key);
  if (hit) return hit;

  const out = new Float64Array(6);
  for (let s = 0; s < COMP_SLOTS; s++) {
    const n = comp[s]!;
    if (n === 0) continue;
    const p = n / remaining;
    comp[s] = n - 1;
    const next = add(total, soft, SLOT_VALUE[s]!);
    const sub = dealerOutcome(comp, next.total, next.soft, rules, memo);
    comp[s] = n;
    for (let i = 0; i < 6; i++) out[i] = out[i]! + p * sub[i]!;
  }
  memo.set(key, out);
  return out;
}

/**
 * The dealer's distribution GIVEN that the peek has already ruled out a natural.
 *
 * ============================ WHY THIS EXISTS ==================================
 * This game offers insurance, which means the dealer peeks at the hole card before
 * play continues. So by the time the player acts against a ten or an ace, one outcome
 * is already known not to have happened: the dealer does not have blackjack. Every
 * subsequent EV has to be conditioned on that, or the player is being told the value
 * of a decision in a game they are not playing.
 *
 * It is not a small correction. Computed without it, hard eleven against a ten and
 * against an ace both came out as HITS, where every published double-deck chart doubles
 * them — because the un-conditioned model was quietly charging the player for losing a
 * doubled bet to a dealer natural that the peek has already excluded. That was the
 * signal that this was missing; the cells against every other upcard were already
 * correct, which is exactly the fingerprint of a peek bug.
 *
 * Removing the natural branch and renormalising is the whole fix. For upcards that
 * cannot make a natural it is a no-op, so callers pass through it unconditionally.
 * ================================================================================
 */
export function dealerAfterPeek(
  comp: Int32Array, upValue: number, rules: BlackjackRules,
  memo: Map<string, Float64Array> = new Map(),
): Float64Array {
  /**
   * Memoised separately from `dealerOutcome`, under its own key prefix. The
   * renormalisation is cheap but it was being redone at every node of the player's hit
   * recursion, which is where the tail latency was.
   */
  const pk = `peek|${upValue}|${compKey(comp)}`;
  const cached = memo.get(pk);
  if (cached) return cached;
  const raw = dealerOutcome(comp, upValue, upValue === 11, rules, memo);
  if (upValue !== 10 && upValue !== 11) { memo.set(pk, raw); return raw; }

  let remaining = 0;
  for (let s = 0; s < COMP_SLOTS; s++) remaining += comp[s]!;
  if (remaining === 0) return raw;

  // The hole card that would make a natural: a ten under an ace, an ace under a ten.
  const pNatural = (upValue === 11 ? comp[8]! : comp[ACE_SLOT]!) / remaining;
  if (pNatural <= 0) { memo.set(pk, raw); return raw; }
  if (pNatural >= 1) { const out = new Float64Array(6); out[4] = 1; memo.set(pk, out); return out; }

  const out = new Float64Array(6);
  for (let i = 0; i < 6; i++) out[i] = raw[i]!;
  out[4] = Math.max(0, out[4]! - pNatural);   // naturals are the 21s the peek excluded
  const total = out.reduce((a, x) => a + x, 0);
  for (let i = 0; i < 6; i++) out[i] = out[i]! / total;
  memo.set(pk, out);
  return out;
}

/** What a finished player total is worth against the dealer's distribution. */
export function standEv(total: number, dealer: Float64Array): number {
  if (total > 21) return -1;
  let ev = dealer[BUST]!;                       // dealer busts, player wins
  for (let i = 0; i < 5; i++) {
    const dt = 17 + i;
    if (total > dt) ev += dealer[i]!;
    else if (total < dt) ev -= dealer[i]!;
  }
  return ev;
}

/**
 * The value of hitting, played optimally from there on.
 *
 * This is the only place a maximisation appears, and it has to: after taking a card
 * the player may hit again or stand, and the value of hitting now depends on playing
 * the rest correctly. Doubling is excluded from the recursion because the rules only
 * permit it on the first two cards.
 */
export function hitEv(
  comp: Int32Array, total: number, soft: boolean,
  dUp: number, dTotal: number, dSoft: boolean, rules: BlackjackRules,
  memo: Map<string, Float64Array>,
  hitMemo: Map<string, number> = new Map(),
): number {
  /**
   * The tail latency lived here. Without this the recursion re-derives the same
   * (total, soft, composition) node once per ORDER in which those cards could have
   * arrived, which is combinatorial: median was fine at 9ms while p95 hit 195ms and
   * the worst decision took 900ms, against a 50ms target. Different draw orders reach
   * identical states constantly, so the cache hit rate is high.
   */
  const hk = `${total}|${soft ? 1 : 0}|${compKey(comp)}`;
  const done = hitMemo.get(hk);
  if (done !== undefined) return done;

  let remaining = 0;
  for (let s = 0; s < COMP_SLOTS; s++) remaining += comp[s]!;
  if (remaining === 0) return standEv(total, dealerAfterPeek(comp, dTotal, rules, memo));

  let ev = 0;
  for (let s = 0; s < COMP_SLOTS; s++) {
    const n = comp[s]!;
    if (n === 0) continue;
    const p = n / remaining;
    comp[s] = n - 1;
    const next = add(total, soft, SLOT_VALUE[s]!);
    let best: number;
    if (next.total > 21) {
      best = -1;
    } else {
      const s1 = standEv(next.total, dealerAfterPeek(comp, dTotal, rules, memo));
      const h1 = hitEv(comp, next.total, next.soft, dUp, dTotal, dSoft, rules, memo, hitMemo);
      best = Math.max(s1, h1);
    }
    comp[s] = n;
    ev += p * best;
  }
  hitMemo.set(hk, ev);
  return ev;
}

/** Doubling: exactly one card, twice the stake, no further decisions. */
export function doubleEv(
  comp: Int32Array, total: number, soft: boolean,
  dTotal: number, dSoft: boolean, rules: BlackjackRules,
  memo: Map<string, Float64Array>,
): number {
  let remaining = 0;
  for (let s = 0; s < COMP_SLOTS; s++) remaining += comp[s]!;
  if (remaining === 0) return 2 * standEv(total, dealerAfterPeek(comp, dTotal, rules, memo));

  let ev = 0;
  for (let s = 0; s < COMP_SLOTS; s++) {
    const n = comp[s]!;
    if (n === 0) continue;
    const p = n / remaining;
    comp[s] = n - 1;
    const next = add(total, soft, SLOT_VALUE[s]!);
    const v = next.total > 21
      ? -1
      : standEv(next.total, dealerAfterPeek(comp, dTotal, rules, memo));
    comp[s] = n;
    ev += p * v;
  }
  return 2 * ev;                                 // the stake is doubled, win or lose
}

/**
 * Insurance, which is a side bet on the dealer's hole card being a ten and nothing
 * else. Independent of the player's hand entirely — a fact worth encoding, because
 * "I have a good hand so I should protect it" is the single most common reason people
 * take it.
 *
 * Half a bet, paying 2:1, so it breaks even when tens are exactly a third of what is
 * left. In a two-deck game that needs 1/3 of the remaining cards to be tens, and a
 * fresh shoe holds 32 of 104, which is below it. Returned per unit of the MAIN bet.
 */
export function insuranceEv(comp: Int32Array, rules: BlackjackRules): number {
  let remaining = 0;
  for (let s = 0; s < COMP_SLOTS; s++) remaining += comp[s]!;
  if (remaining === 0) return 0;
  const pTen = comp[8]! / remaining;
  return 0.5 * (pTen * rules.insurancePays - (1 - pTen));
}
